import { useEffect, useMemo, useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type { AgentAttachment } from '@/services/agent/service'
import { libraryStoryService } from '@/services/campaigns'

type Props = {
    open: boolean
    onOpenChange: (open: boolean) => void
    onAdd: (attachments: AgentAttachment[]) => void
}

type StoryRow = {
    _id: string
    title: string
    subject_type?: 'person' | 'company' | 'product'
    subject_name?: string
    story_strength?: 'high' | 'medium' | 'low'
    story_tags?: string[]
    createdAt?: string
}
type StoryTab = 'all' | 'person' | 'company' | 'product'

export default function HeftStoryPickerDialog({ open, onOpenChange, onAdd }: Props) {
    const [tab, setTab] = useState<StoryTab>('all')
    const [query, setQuery] = useState('')
    const [debouncedQuery, setDebouncedQuery] = useState('')
    const [stories, setStories] = useState<StoryRow[]>([])
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        const t = setTimeout(() => setDebouncedQuery(query), 250)
        return () => clearTimeout(t)
    }, [query])

    useEffect(() => {
        if (!open) return
        let cancelled = false
        setIsLoading(true)
        libraryStoryService
            .listStories({
                search: debouncedQuery.trim() || undefined,
                subjectType: tab === 'all' ? undefined : tab,
                limit: debouncedQuery.trim() ? 30 : 15,
                skip: 0,
            })
            .then(res => {
                if (cancelled) return
                const payload = (res as any)?.data !== undefined ? (res as any).data : res
                const list = Array.isArray(payload?.stories) ? payload.stories : []
                const rows: StoryRow[] = (list as any[])
                    .map((s): StoryRow => ({
                        _id: String(s._id || s.id),
                        title: String(s.title || ''),
                        subject_type: s.subject_type,
                        subject_name: typeof s.subject_name === 'string' ? s.subject_name : undefined,
                        story_strength: s.story_strength,
                        story_tags: Array.isArray(s.story_tags) ? s.story_tags : [],
                        createdAt: typeof s.createdAt === 'string' ? s.createdAt : undefined,
                    }))
                    .filter((r: StoryRow) => r._id.length > 0 && r.title.length > 0)
                setStories(rows)
            })
            .catch(() => {
                if (cancelled) return
                setStories([])
            })
            .finally(() => {
                if (cancelled) return
                setIsLoading(false)
            })

        return () => {
            cancelled = true
        }
    }, [open, tab, debouncedQuery])

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase()
        if (!q) return stories
        return stories.filter(s => s.title.toLowerCase().includes(q))
    }, [stories, query])

    const attach = (story: StoryRow) => {
        onAdd([
            {
                kind: 'heft_story',
                id: story._id,
                title: story.title,
                subjectType: story.subject_type,
                subjectName: story.subject_name,
                storyStrength: story.story_strength,
                storyTags: story.story_tags,
                createdAt: story.createdAt,
            },
        ])
        onOpenChange(false)
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='max-w-xl'>
                <DialogHeader>
                    <DialogTitle>Attach a HEFT story</DialogTitle>
                </DialogHeader>

                <div className='space-y-3'>
                    <Tabs value={tab} onValueChange={v => setTab(v as StoryTab)}>
                        <TabsList>
                            <TabsTrigger value='all'>All</TabsTrigger>
                            <TabsTrigger value='person'>People</TabsTrigger>
                            <TabsTrigger value='company'>Companies</TabsTrigger>
                            <TabsTrigger value='product'>Products</TabsTrigger>
                        </TabsList>
                        <TabsContent value={tab} className='mt-4 space-y-3'>
                            <Input value={query} onChange={e => setQuery(e.target.value)} placeholder='Search stories…' />

                            <div className='max-h-80 overflow-y-auto rounded-lg border border-slate-200 bg-white'>
                                {isLoading ? (
                                    <div className='p-3 text-sm text-slate-500'>Loading…</div>
                                ) : filtered.length === 0 ? (
                                    <div className='p-3 text-sm text-slate-500'>
                                        {debouncedQuery.trim() ? 'No stories found.' : 'No recent stories found.'}
                                    </div>
                                ) : (
                                    <div className='divide-y divide-slate-100'>
                                        {filtered.slice(0, 50).map(story => (
                                            <button
                                                key={story._id}
                                                type='button'
                                                onClick={() => attach(story)}
                                                className='w-full px-3 py-2 text-left hover:bg-slate-50'
                                            >
                                                <div className='flex items-center gap-3'>
                                                    <div className='flex-1 min-w-0'>
                                                        <div className='truncate text-sm font-medium text-slate-900'>
                                                            {story.title}
                                                        </div>
                                                        <div className='text-xs text-slate-500'>
                                                            {(() => {
                                                                const parts: string[] = []
                                                                parts.push(story.subject_type || 'story')
                                                                if (story.subject_name) parts.push(story.subject_name)
                                                                if (story.story_strength) parts.push(`strength: ${story.story_strength}`)
                                                                if ((story.story_tags || []).length) parts.push((story.story_tags || []).slice(0, 3).join(', '))
                                                                return parts.join(' · ')
                                                            })()}
                                                        </div>
                                                    </div>
                                                    <span className='shrink-0 whitespace-nowrap text-xs font-semibold text-primary-main'>Attach</span>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </TabsContent>
                    </Tabs>
                </div>
            </DialogContent>
        </Dialog>
    )
}

